import React, { useEffect, useState } from 'react';
import {
  Button,
  Stepper,
  Step,
  StepLabel,
  DialogContent,
  DialogActions,
  Box,
  Typography
} from '@mui/material';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import Loader from 'src/common/Loader';
import { setErrorInfo, setSuccessInfo } from 'src/redux/modalSlice';
import { useDispatch } from 'react-redux';
import { getAllDispensers } from 'src/redux/dispenserSlice';
import { addNewCard, updateCardDetails } from 'src/redux/cardSlice';
import { getVehicleList } from 'src/redux/vehicleSlice';
import { getOperatorList } from 'src/redux/operatorSlice';
import { getAllSites } from 'src/redux/siteSlice';
import FMSModal from 'src/common/FMSModal';
import { getDriverList } from 'src/redux/driverSlice';
import CardDetailsForm from './forms/CardDetailsForm';
import AdditionalConfigForm from './forms/AdditionalConfigForm';
import CardConfigForm from './forms/CardConfigForm';
import validationSchema from './formModel/cardValidation';
import cardFormModel from './formModel/cardsFormModel';
import formInitialValues from './formModel/cardInitialValues';
import { getFormattedCardBody } from './formModel/cardFormUtils';

const { formId, formField } = cardFormModel;
const DROPDOWN_PAGE_SIZE = 1000;

function renderStepContent(step, initialValues, extraProps = {}) {
  switch (step) {
    case 0:
      return (
        <CardDetailsForm
          formField={formField}
          initValues={!!initialValues}
          {...extraProps}
        />
      );
    case 1:
      return <CardConfigForm formField={formField} />;
    case 2:
      return <AdditionalConfigForm formField={formField} />;
    default:
      return null;
  }
}

const NewCard = ({ open, handleClose, initialValues }) => {
  const steps = [
    'Card Details',
    'Card Configuration',
    'Additional Configuration'
  ];

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const currentValidationSchema = validationSchema[activeStep];
  const isLastStep = activeStep === steps.length - 1;
  const formInitValues = initialValues || formInitialValues;
  const isEditMode = Boolean(initialValues);
  const requiredHintText = '';

  const loadSiteScopedDropdowns = (siteId) => {
    if (!siteId) return;

    const requestPayload = {
      limit: DROPDOWN_PAGE_SIZE,
      pageNo: 1,
      params: { site: siteId }
    };

    dispatch(getVehicleList(requestPayload));
    dispatch(getOperatorList(requestPayload));
    dispatch(getDriverList(requestPayload));
  };

  useEffect(() => {
    if (open) {
      dispatch(getAllSites());
      dispatch(getAllDispensers());
      if (initialValues?.site) {
        loadSiteScopedDropdowns(initialValues.site);
      }
    }
    setActiveStep(0);
  }, [dispatch, initialValues?.site, open]);

  const handleBack = () => {
    if (activeStep === 0) {
      handleClose();
    } else {
      setActiveStep(activeStep - 1);
    }
  };

  const submitForm = async (values, actions) => {
    const cardReqBody = getFormattedCardBody(values);
    setLoading(true);
    try {
      if (initialValues) {
        await dispatch(updateCardDetails(cardReqBody)).unwrap();
      } else {
        await dispatch(addNewCard(cardReqBody)).unwrap();
      }

      dispatch(
        setSuccessInfo({
          successMsg: initialValues
            ? 'Card details updated successfully!'
            : 'New Card has been created successfully!'
        })
      );

      handleClose();
    } catch (error) {
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
      actions.setSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (values, actions) => {
    if (isLastStep) {
      submitForm(values, actions);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  };

  const getActionButton = () => {
    if (!isLastStep) return 'Next';
    return initialValues ? 'Update Card' : 'Create Card';
  };

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {/* ---------- Modern Header ---------- */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography fontWeight={700} fontSize={18}>
          {initialValues ? 'UPDATE CARD' : 'ADD NEW CARD'}
        </Typography>
      </Box>

      <DialogContent
        sx={{
          px: 3,
          py: 0,
          overflowX: 'hidden'
        }}
      >
        {!loading && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 5,
              mx: -3,
              px: 3,
              pt: 1.5,
              pb: 1,
              backgroundColor: '#FFFFFF',
              borderBottom: '1px solid #E5E7EB'
            }}
          >
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                maxWidth: 760,
                mx: 'auto',
                '& .MuiStep-root': {
                  px: { xs: 0.5, sm: 1 }
                },
                '& .MuiStepLabel-labelContainer': {
                  mt: 0.5
                },
                '& .MuiStepLabel-label': {
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#64748B !important'
                },
                '& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed': {
                  fontWeight: 700,
                  color: '#0B6E99 !important'
                },
                '& .MuiStepIcon-root': {
                  color: '#CBD5E1 !important'
                },
                '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': {
                  color: '#0B6E99 !important'
                },
                '& .MuiStepConnector-line': {
                  borderColor: '#CBD5E1'
                },
                '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line, & .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                  borderColor: '#0B6E99'
                }
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        )}

        <Formik
          initialValues={formInitValues}
          validationSchema={currentValidationSchema}
          onSubmit={handleSubmit}
        >
          {({
            isSubmitting, handleChange, setFieldValue, values
          }) => {
            if (loading) {
              return <Loader />;
            }

            const handleSiteChange = (e) => {
              const selectedSiteId = e.target.value;
              handleChange(e);
              setFieldValue(formField.vehicleId.name, null);
              setFieldValue(formField.operatorId.name, null);
              setFieldValue(formField.driverId.name, null);
              loadSiteScopedDropdowns(selectedSiteId);
            };

            return (
              <Form id={formId}>

                {/* ---------- Form Wrapper ---------- */}
                <Box
                  sx={{
                    mt: 3,
                    p: 3,
                    border: '1px solid #E5E7EB',
                    borderRadius: 2,
                    backgroundColor: '#FFFFFF'
                  }}
                >
                  {!isEditMode && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      * Required fields
                    </Typography>
                  )}
                  {renderStepContent(activeStep, initialValues, {
                    hasSelectedSite: Boolean(values.site),
                    onSiteChange: handleSiteChange,
                    requiredHintText
                  })}
                </Box>

                {/* ---------- Actions ---------- */}
                <DialogActions
                  sx={{
                    mt: 3,
                    mx: -3,
                    px: 3,
                    py: 2,
                    justifyContent: 'flex-end',
                    gap: 2,
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 5,
                    backgroundColor: '#FFFFFF',
                    borderTop: '1px solid #E5E7EB'
                  }}
                >
                  {activeStep === 0 ? (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleClose}
                      sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      Close
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={handleBack}
                      sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      Back
                    </Button>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3
                    }}
                  >
                    {getActionButton()}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </FMSModal>
  );
};

NewCard.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default NewCard;
